import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon, XIcon, LayersIcon, CheckIcon, Trash2Icon } from 'lucide-react';
import { CatalogItem, CatalogOptionValue } from '../../data/catalogItems';
import { CategoryBlueprint } from '../../data/productCategories';
interface Group {
  name: string;
  values: CatalogOptionValue[];
}
interface Combination {
  key: string;
  labels: string[];
  price: string;
  stock: string;
}

/** Whether this trade prices each choice separately (a 100ml vs a 50ml bottle). */
const PRICES_PER_OPTION: Record<string, boolean> = {
  rental: true,
  sale: true,
  retail: true,
  menu: true,
  unit: true,
  service: true,
  lease: true
};

/**
 * Options, in merchant language — never "variants". Everything offered here is
 * driven by the item's blueprint, so a fleet is asked about rental periods and
 * an apparel shop about size and colour.
 */
export function OptionsEditor({
  item,
  bp
}: {
  item: CatalogItem;
  bp: CategoryBlueprint;
}) {
  const [groups, setGroups] = useState<Group[]>(() => item.options.length ? item.options.map(g => ({
    name: g.name,
    values: g.values.map(v => ({
      ...v
    }))
  })) : bp.optionGroups.map(g => ({
    name: g.name,
    values: g.values.map(v => ({
      label: v
    }))
  })));
  /** Which group is currently taking a new value, and the draft itself. */
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    label: '',
    price: '',
    stock: ''
  });
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [combos, setCombos] = useState<Combination[] | null>(null);
  const tracksStock = bp.availability === 'stock' || bp.availability === 'slots';
  const tracksPrice = PRICES_PER_OPTION[bp.pricing] ?? false;

  /** Group names this trade suggests that the merchant hasn't used yet. */
  const suggestedGroups = useMemo(() => bp.optionGroups.map(g => g.name).filter(n => !groups.some(g => g.name === n)), [bp.optionGroups, groups]);

  /** Values this trade suggests for a group, minus the ones already added. */
  const suggestionsFor = (group: Group) => {
    const source = bp.optionGroups.find(g => g.name === group.name);
    if (!source) return [];
    return source.values.filter(v => !group.values.some(x => x.label === v));
  };
  const addValue = (name: string, label: string, price = '', stock = '') => {
    const clean = label.trim();
    if (!clean) return;
    setGroups(prev => prev.map(g => g.name === name && !g.values.some(v => v.label === clean) ? {
      ...g,
      values: [...g.values, {
        label: clean,
        ...(price.trim() ? {
          price: /^\d/.test(price.trim()) ? `AED ${price.trim()}` : price.trim()
        } : {}),
        ...(stock.trim() ? {
          stock: Number(stock)
        } : {})
      }]
    } : g));
    setCombos(null);
    setDraft({
      label: '',
      price: '',
      stock: ''
    });
  };
  const removeValue = (name: string, label: string) => {
    setGroups(prev => prev.map(g => g.name === name ? {
      ...g,
      values: g.values.filter(v => v.label !== label)
    } : g));
    setCombos(null);
  };
  const addGroup = (name: string) => {
    const clean = name.trim();
    if (!clean || groups.some(g => g.name === clean)) return;
    const seed = bp.optionGroups.find(g => g.name === clean);
    setGroups(prev => [...prev, {
      name: clean,
      // A suggested group arrives with this trade's usual choices already in.
      values: seed ? seed.values.map(v => ({
        label: v
      })) : []
    }]);
    setGroupName('');
    setAddingGroup(false);
    setCombos(null);
  };
  const removeGroup = (name: string) => {
    setGroups(prev => prev.filter(g => g.name !== name));
    setCombos(null);
  };

  /** Every pairing across the groups, so each can carry its own price or stock. */
  const buildCombinations = () => {
    const usable = groups.filter(g => g.values.length > 0);
    if (usable.length < 2) return;
    let rows: string[][] = [[]];
    usable.forEach(g => {
      rows = rows.flatMap(r => g.values.map(v => [...r, v.label]));
    });
    setCombos(rows.slice(0, 60).map(labels => ({
      key: labels.join(' / '),
      labels,
      price: '',
      stock: ''
    })));
  };
  const usableGroups = groups.filter(g => g.values.length > 0);
  const canCombine = usableGroups.length >= 2;
  return <div data-id="element-1094" data-mp="3oia8icio8yms">
      <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1095" data-mp="3oijpxbpnhxgv">
        <LayersIcon className="w-4 h-4 text-slate-400" data-id="element-1096" data-mp="71sd70r4hirs" /> Options
      </h4>

      <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5 space-y-5" data-id="element-1097" data-mp="22z1ndvec1u9">
        {groups.length === 0 && <p className="text-[13px] text-slate-500 leading-relaxed" data-id="element-1098" data-mp="3ajka8tk48swu">
            No options yet. Add one if this {bp.itemNoun} comes in more than one
            choice.
          </p>}

        {groups.map(group => <div key={group.name} data-id="element-1099" data-mp="3jhdw1ak9845r">
            <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1100" data-mp="3fhvm19tz1dhp">
              <p className="text-xs font-semibold text-slate-500" data-id="element-1101" data-mp="ythnucu5z2l2">
                {group.name}
              </p>
              <button onClick={() => removeGroup(group.name)} className="text-[11px] font-bold text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-1102" data-mp="usfmnqgosggg">
                Remove
              </button>
            </div>

            <div className="flex flex-wrap gap-2" data-id="element-1103" data-mp="1f6ilgpo1ejr">
              {group.values.map(v => <span key={v.label} className="group inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-xl bg-white border border-slate-200 text-sm" data-id="element-1104" data-mp="1dbszp3g8y9c">
                  <span className="font-semibold text-slate-800" data-id="element-1105" data-mp="39uzpd3u73bnm">
                    {v.label}
                  </span>
                  {v.price && <span className="text-xs font-bold text-primary-indigo" data-id="element-1106" data-mp="3dv0ebiex758x">
                      {v.price}
                    </span>}
                  {typeof v.stock === 'number' && <span className="text-[11px] text-slate-400" data-id="element-1107" data-mp="2m55c4l5yrl2u">
                      {v.stock} left
                    </span>}
                  <button onClick={() => removeValue(group.name, v.label)} aria-label={`Remove ${v.label}`} className="w-5 h-5 rounded-md flex items-center justify-center text-slate-300 hover:bg-slate-100 hover:text-error transition-colors duration-150 ease-out" data-id="element-1108" data-mp="2inzs8kgyori2">
                    <XIcon className="w-3 h-3" data-id="element-1109" data-mp="24n6xpzqntwey" />
                  </button>
                </span>)}

              {addingTo !== group.name && <button onClick={() => {
            setAddingTo(group.name);
            setDraft({
              label: '',
              price: '',
              stock: ''
            });
          }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-slate-300 text-xs font-bold text-slate-500 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1110" data-mp="1uxdmx0ycdlbr">
                  <PlusIcon className="w-3.5 h-3.5" data-id="element-1111" data-mp="2zwin62t3dufx" />
                  Add option
                </button>}
            </div>

            <AnimatePresence data-id="element-1112" data-mp="2qvav42h65pua">
              {addingTo === group.name && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="mt-3 p-3 bg-white border border-slate-200 rounded-2xl" data-id="element-1113" data-mp="2qxdjki2obaww">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1114" data-mp="1y6vljvgt3zqc">
                    <input autoFocus value={draft.label} onChange={e => setDraft(d => ({
                ...d,
                label: e.target.value
              }))} onKeyDown={e => {
                if (e.key === 'Enter') addValue(group.name, draft.label, draft.price, draft.stock);
                if (e.key === 'Escape') setAddingTo(null);
              }} placeholder={`New ${group.name.toLowerCase()}`} className="flex-1 min-w-[8rem] px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1115" data-mp="235q18ojh9868" />
                    {tracksPrice && <div className="relative" data-id="element-1116" data-mp="16mkz1r6iu4az">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400" data-id="element-1117" data-mp="11t08ulb66emc">
                          AED
                        </span>
                        <input value={draft.price} onChange={e => setDraft(d => ({
                  ...d,
                  price: e.target.value
                }))} placeholder="Price" inputMode="decimal" className="w-28 pl-11 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-1118" data-mp="rwdoryqo3rld" />
                      </div>}
                    {tracksStock && <input value={draft.stock} onChange={e => setDraft(d => ({
                ...d,
                stock: e.target.value
              }))} placeholder="Stock" inputMode="numeric" className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-1119" data-mp="2bpye1ks7arvj" />}
                    <button disabled={!draft.label.trim()} onClick={() => addValue(group.name, draft.label, draft.price, draft.stock)} className="px-3.5 py-2 bg-primary-indigo text-white text-xs font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out inline-flex items-center gap-1.5" data-id="element-1120" data-mp="38h8w7voh9sd5">
                      <CheckIcon className="w-3.5 h-3.5" data-id="element-1121" data-mp="2lfqjyqvmndgw" />
                      Add
                    </button>
                    <button onClick={() => setAddingTo(null)} className="px-2 py-2 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors duration-150 ease-out" data-id="element-1122" data-mp="30chofek5rfdd">
                      Cancel
                    </button>
                  </div>

                  {/* What this trade would usually offer, one tap away */}
                  {suggestionsFor(group).length > 0 && <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-3 border-t border-slate-100" data-id="element-1123" data-mp="3e5z76x50so2c">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-1124" data-mp="1llfw8nciwz1">
                        Common
                      </span>
                      {suggestionsFor(group).map(s => <button key={s} onClick={() => addValue(group.name, s)} className="px-2.5 py-1 rounded-lg bg-slate-50 text-[12px] font-semibold text-slate-600 hover:bg-primary-indigo/[0.06] hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1125" data-mp="3ns9yngxo7rap">
                          {s}
                        </button>)}
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </div>)}

        {/* Another kind of choice — seeded from this trade where possible */}
        <div className="pt-1 border-t border-slate-100" data-id="element-1126" data-mp="303qo0ur5f2fg">
          {addingGroup ? <div className="flex flex-wrap items-center gap-2 pt-4" data-id="element-1127" data-mp="2t0tufq9asnb7">
              <input autoFocus value={groupName} onChange={e => setGroupName(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') addGroup(groupName);
            if (e.key === 'Escape') setAddingGroup(false);
          }} placeholder="What varies? e.g. Size" className="flex-1 min-w-[10rem] px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1128" data-mp="2t24y7q2qtv2b" />
              <button disabled={!groupName.trim()} onClick={() => addGroup(groupName)} className="px-3.5 py-2 bg-primary-indigo text-white text-xs font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-1129" data-mp="1vemuwurupl5p">
                Add
              </button>
              <button onClick={() => setAddingGroup(false)} className="px-2 py-2 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors duration-150 ease-out" data-id="element-1130" data-mp="62pvz14wbb3z">
                Cancel
              </button>
              {suggestedGroups.length > 0 && <div className="w-full flex items-center gap-1.5 flex-wrap mt-1" data-id="element-1131" data-mp="3faag1hh7x8si">
                  {suggestedGroups.map(s => <button key={s} onClick={() => addGroup(s)} className="px-2.5 py-1 rounded-lg bg-slate-50 text-[12px] font-semibold text-slate-600 hover:bg-primary-indigo/[0.06] hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1132" data-mp="3jc49p5szxcj6">
                      {s}
                    </button>)}
                </div>}
            </div> : <div className="flex flex-wrap items-center gap-3 pt-4" data-id="element-1133" data-mp="iw76ornb8n9j">
              <button onClick={() => setAddingGroup(true)} className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-1134" data-mp="du81dc80fg9y">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1135" data-mp="3mhplaqf2b0" />
                Add another choice
              </button>
              <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-1136" data-mp="3ra629s6de6mi" />
              <button onClick={buildCombinations} disabled={!canCombine} title={canCombine ? undefined : 'Add a second choice first — combinations pair two or more.'} className="text-xs font-bold text-primary-indigo hover:text-indigo-700 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors duration-150 ease-out" data-id="element-1137" data-mp="3r7tayoeq2jws">
                Create combinations
              </button>
              {!canCombine && <span className="text-[11px] text-slate-400" data-id="element-1138" data-mp="1stoqaxus96s0">
                  Needs two choices, like {bp.optionGroups[0]?.name ?? 'Size'}{' '}
                  and Colour.
                </span>}
            </div>}
        </div>

        {/* Each pairing, so a large sand shirt can differ from a small one */}
        <AnimatePresence data-id="element-1139" data-mp="2bqyc2lnkywcf">
          {combos && <motion.div initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-id="element-1140" data-mp="2bpnbeupfm4pj">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100" data-id="element-1141" data-mp="1i1zpku4u55s5">
                <p className="text-xs font-bold text-slate-900" data-id="element-1142" data-mp="1d0k3qzuu2v8p">
                  {combos.length} combination{combos.length === 1 ? '' : 's'}
                </p>
                <button onClick={() => setCombos(null)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-1143" data-mp="141ylz6x6rk8z">
                  <Trash2Icon className="w-3.5 h-3.5" data-id="element-1144" data-mp="1wqw336pnvb05" />
                  Clear
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-id="element-1145" data-mp="1n0az13r7xe9u">
                {combos.map((c, i) => <div key={c.key} className="flex items-center gap-3 px-4 py-2.5" data-id="element-1146" data-mp="2kg6v9zsy3x2o" data-mpi={i}>
                    <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0" data-id="element-1147" data-mp="2fnnn5id11u59">
                      {c.labels.map(l => <span key={l} className="px-2 py-0.5 rounded-lg bg-slate-50 text-[12px] font-semibold text-slate-600" data-id="element-1148" data-mp="2ryy2kfs3ynbw">
                          {l}
                        </span>)}
                    </div>
                    {tracksPrice && <div className="relative flex-shrink-0" data-id="element-1149" data-mp="35yfv968iykqj">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400" data-id="element-1150" data-mp="3axaeg9aembb6">
                          AED
                        </span>
                        <input value={c.price} onChange={e => setCombos(prev => prev ? prev.map((x, xi) => xi === i ? {
                  ...x,
                  price: e.target.value
                } : x) : prev)} placeholder={item.priceLabel.replace('AED ', '')} inputMode="decimal" className="w-28 pl-10 pr-2 py-1.5 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-1151" data-mp="3knltorzjg6xd" />
                      </div>}
                    {tracksStock && <input value={c.stock} onChange={e => setCombos(prev => prev ? prev.map((x, xi) => xi === i ? {
                ...x,
                stock: e.target.value
              } : x) : prev)} placeholder="Stock" inputMode="numeric" className="w-20 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[13px] text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-1152" data-mp="206oheshp3255" />}
                  </div>)}
              </div>
              <p className="px-4 py-3 text-[11px] text-slate-400 border-t border-slate-100 leading-relaxed" data-id="element-1153" data-mp="3bxm90br0b81r">
                Leave a row blank to use the {bp.itemNoun}'s main{' '}
                {tracksPrice ? 'price' : 'availability'}.
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}